import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import { isLocalOnlyUrl, stripImageFileExtension } from '@/utils/imageUrlUtils';
import { pickImageSignOptions } from '@/utils/imageDisplayAccess';
import i18n from '@/i18n';
import { useSignedImageSrc } from '@/hooks/useSignedImageSrc';
import { resolveImageSpaceCreatorLabel } from '@/pages/ipe/workbench/panels/imageSpaceUtils';
import {
  getImageSpaceSourceMeta,
  resolveImageSpaceSourceDetail,
} from '@/pages/ipe/workbench/constants/imageSpaceSourceOptions';
import { formatStorageBytesLabel, resolveImageDisplayByteSize } from '@/pages/ipe/workbench/utils/imageSpaceStorageStats';
import { buildImageSpacePreviewImage } from '@/pages/ipe/workbench/utils/buildImageSpacePreviewImage';
import { downloadImageSpaceImage } from '@/pages/ipe/workbench/utils/downloadImageSpaceImage';
import { useWorkbenchImagePreview } from '@/pages/ipe/workbench/hooks/useWorkbenchImagePreview';

function metaRow(label, value) {
  return (
    <div className="funding-image-space-preview__meta-row">
      <span className="funding-image-space-preview__meta-label">{label}</span>
      <span className="funding-image-space-preview__meta-value">{value}</span>
    </div>
  );
}

function inferImageFormatLabel(previewImage, previewRaw) {
  const stored = String(previewImage?.format || '').trim().replace(/^\./, '');
  if (stored && stored !== '—') return stored.replace(/^jpeg$/i, 'jpg');

  const fromName = (previewImage?.name || '').match(/\.([a-zA-Z0-9]+)$/);
  if (fromName) return fromName[1].toLowerCase();

  const mimeFromDataUrl = (s) => {
    if (typeof s !== 'string' || !s.startsWith('data:image/')) return '';
    const mime = s.slice('data:image/'.length).split(';')[0].split('+')[0].toLowerCase();
    if (mime === 'jpeg') return 'jpg';
    return mime || '';
  };

  const fromPreviewMime = mimeFromDataUrl(previewRaw);
  if (fromPreviewMime) return fromPreviewMime;

  const urlCandidates = [previewImage?.url, previewRaw].filter(
    (x) => typeof x === 'string' && x.trim() && !x.startsWith('blob:'),
  );
  for (const u of urlCandidates) {
    const pathOnly = u.split('?')[0].replace(/\\/g, '/');
    const fromPath = pathOnly.match(/\.([a-zA-Z0-9]+)$/);
    if (fromPath) return fromPath[1].toLowerCase();
  }

  const fromStoredData = mimeFromDataUrl(previewImage?.dataUrl);
  if (fromStoredData) return fromStoredData;

  return '—';
}

export default function ImageSpacePreviewModal({
  open,
  initialImage,
  navList = [],
  onClose,
  resolveUrl,
  authUserId = '',
  authDisplayName = '',
  showTrashAction = false,
  onMoveToTrash,
  onSourceNavigate,
  onDownloadError,
}) {
  const [loading, setLoading] = useState(false);
  const navListRef = useRef(navList);
  const {
    previewImage,
    setPreviewImage,
    previewScale,
    previewOffset,
    isPreviewDragging,
    previewContainerRef,
    closePreview,
    resetPreviewView,
    handlePreviewPointerDown,
    handlePreviewPointerMove,
    handlePreviewPointerUp,
  } = useWorkbenchImagePreview();

  navListRef.current = navList;
  // 调用方常传内联 resolveUrl，勿放进 useCallback/effect 依赖，否则会 Maximum update depth
  const resolveUrlRef = useRef(resolveUrl);
  resolveUrlRef.current = resolveUrl;

  const loadPreview = useCallback(async (image) => {
    if (!image?.id) return;
    setLoading(true);
    try {
      const built = await buildImageSpacePreviewImage(image, {
        resolveUrl: (...args) => resolveUrlRef.current?.(...args),
      });
      if (built) {
        setPreviewImage(built);
        resetPreviewView();
      }
    } finally {
      setLoading(false);
    }
  }, [resetPreviewView, setPreviewImage]);

  useEffect(() => {
    if (!open || !initialImage) {
      closePreview();
      return undefined;
    }
    loadPreview(initialImage);
    return undefined;
    // loadPreview / closePreview 已稳定；只跟开关与图片 id 走
  }, [open, initialImage?.id, loadPreview, closePreview]);

  const handleClose = useCallback(() => {
    closePreview();
    onClose?.();
  }, [closePreview, onClose]);

  const stepPreview = useCallback(async (delta) => {
    const list = navListRef.current;
    if (!Array.isArray(list) || list.length === 0 || !previewImage) return;
    const idx = list.findIndex((item) => String(item.id) === String(previewImage.id));
    if (idx < 0) return;
    const target = list[idx + delta];
    if (!target) return;
    await loadPreview(target);
  }, [loadPreview, previewImage]);

  useEffect(() => {
    if (!open || !previewImage) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        stepPreview(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        stepPreview(1);
      } else if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, previewImage, stepPreview, handleClose]);

  const navIndex = useMemo(
    () => navList.findIndex((item) => String(item.id) === String(previewImage?.id)),
    [navList, previewImage?.id],
  );
  const canPreviewPrev = navIndex > 0;
  const canPreviewNext = navIndex >= 0 && navIndex < navList.length - 1;

  const previewRaw = previewImage
    ? [previewImage.src, previewImage.url, previewImage.dataUrl].find(
      (x) => typeof x === 'string' && x.trim(),
    )
    : '';
  const signedPreviewSrc = useSignedImageSrc(previewRaw, pickImageSignOptions(previewImage));

  if (!open) return null;

  const sourceMeta = previewImage ? getImageSpaceSourceMeta(previewImage) : null;
  const sourceDetail = previewImage ? resolveImageSpaceSourceDetail(previewImage) : null;
  const formatLabel = previewImage ? inferImageFormatLabel(previewImage, previewRaw) : '—';
  const usage = previewImage
    ? Number(previewImage.usageCount ?? previewImage.useCount ?? previewImage.referenceCount ?? 0) || 0
    : 0;
  const resolvedSize = previewImage ? resolveImageDisplayByteSize(previewImage) : null;
  const fileSizeLabel = resolvedSize ? formatStorageBytesLabel(resolvedSize, { digits: 2 }) : '—';

  const handleDownload = () => {
    if (!previewImage) return;
    downloadImageSpaceImage(previewImage, { onError: onDownloadError });
  };

  const handleMoveToTrash = async () => {
    if (!previewImage?.id || !onMoveToTrash) return;
    const currentId = previewImage.id;
    const list = navListRef.current;
    const navIdx = list.findIndex((item) => String(item.id) === String(currentId));
    const nextTarget = list[navIdx + 1] || list[navIdx - 1];
    navListRef.current = list.filter((item) => String(item.id) !== String(currentId));
    await onMoveToTrash(previewImage);
    if (nextTarget) {
      await loadPreview(nextTarget);
    } else {
      handleClose();
    }
  };

  return createPortal(
    <div
      className="funding-image-space-preview-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label={i18n.t('workbench:imagePreview.detailPreview')}
    >
      <div
        className="funding-image-space-preview-stack"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="funding-image-space-preview-shell">
          <button
            type="button"
            className="funding-image-space-preview-nav"
            aria-label={i18n.t('workbench:imagePreview.prev')}
            disabled={!canPreviewPrev || loading}
            onClick={() => stepPreview(-1)}
          >
            <ChevronLeft size={22} strokeWidth={2.25} aria-hidden />
          </button>

          <div className="funding-image-space-preview-dialog">
            <button
              type="button"
              className="funding-image-space-preview-close"
              aria-label={i18n.t('workbench:imagePreview.close')}
              onClick={handleClose}
            >
              <X size={20} strokeWidth={2.25} aria-hidden />
            </button>
            <div
              ref={previewContainerRef}
              className="funding-image-space-preview__viewport"
              style={{ cursor: isPreviewDragging ? 'grabbing' : 'default' }}
            >
              {loading && !previewRaw ? (
                <div className="funding-image-space-preview__placeholder">
                  {i18n.t('workbench:imagePreview.loading')}
                </div>
              ) : previewRaw && !isLocalOnlyUrl(previewRaw) ? (
                <div className="funding-image-space-preview__stage">
                  {signedPreviewSrc ? (
                  <img
                    src={signedPreviewSrc}
                    alt={stripImageFileExtension(previewImage?.name) || previewImage?.id}
                    draggable={false}
                    className="funding-image-space-preview__image"
                    style={{
                      cursor: isPreviewDragging ? 'grabbing' : 'grab',
                      transform: `translate(${previewOffset.x}px, ${previewOffset.y}px) scale(${previewScale})`,
                      transition: isPreviewDragging ? 'none' : 'transform 0.1s ease-out',
                    }}
                    onLoad={(event) => {
                      const el = event.currentTarget;
                      const nw = el.naturalWidth;
                      const nh = el.naturalHeight;
                      if (!nw || !nh) return;
                      setPreviewImage((prev) => {
                        if (!prev) return prev;
                        const w = prev.width != null ? Number(prev.width) : 0;
                        const h = prev.height != null ? Number(prev.height) : 0;
                        const needW = !w || Number.isNaN(w);
                        const needH = !h || Number.isNaN(h);
                        if (!needW && !needH) return prev;
                        return {
                          ...prev,
                          width: needW ? nw : prev.width,
                          height: needH ? nh : prev.height,
                        };
                      });
                    }}
                    onClick={(event) => event.stopPropagation()}
                    onPointerDown={handlePreviewPointerDown}
                    onPointerMove={handlePreviewPointerMove}
                    onPointerUp={handlePreviewPointerUp}
                    onPointerCancel={handlePreviewPointerUp}
                  />
                  ) : (
                    <div className="funding-image-space-preview__placeholder">
                  {i18n.t('workbench:imagePreview.loading')}
                </div>
                  )}
                </div>
              ) : previewRaw && isLocalOnlyUrl(previewRaw) ? (
                <div className="funding-image-space-preview__placeholder funding-image-space-preview__placeholder--error">
                  {i18n.t('workbench:imagePreview.localOnly')}
                </div>
              ) : (
                <div className="funding-image-space-preview__placeholder">
                  {i18n.t('workbench:imagePreview.loadingOrMissing')}
                </div>
              )}
            </div>

            <aside className="funding-image-space-preview__aside">
              <div className="funding-image-space-preview__title">
                {stripImageFileExtension(previewImage?.name)
                  || i18n.t('workbench:imagePreview.unnamed')}
              </div>
              <div className="funding-image-space-preview__section-title">
                {i18n.t('workbench:imagePreview.basicInfo')}
              </div>
              {metaRow(i18n.t('workbench:imagePreview.format'), formatLabel)}
              {metaRow(i18n.t('workbench:imagePreview.fileSize'), fileSizeLabel)}
              {metaRow(
                i18n.t('workbench:imagePreview.dimensions'),
                previewImage?.width && previewImage?.height
                  ? `${previewImage.width} × ${previewImage.height} px`
                  : '—',
              )}
              {metaRow(i18n.t('workbench:imagePreview.createdAt'), previewImage?.createdAt || '—')}
              {metaRow(
                i18n.t('workbench:imagePreview.creator'),
                previewImage
                  ? resolveImageSpaceCreatorLabel(previewImage, { authUserId, authDisplayName })
                  : '—',
              )}
              {sourceMeta ? (
                <div className="funding-image-space-preview__meta-row funding-image-space-preview__meta-row--source">
                  <span className="funding-image-space-preview__meta-label">
                    {i18n.t('workbench:imagePreview.source')}
                  </span>
                  <span className="funding-image-space-preview__meta-value">
                    {sourceMeta.groupLabel ? (
                      <span className="funding-image-space-preview__source-group">
                        {sourceMeta.groupLabel}
                      </span>
                    ) : null}
                    {sourceMeta.path && sourceMeta.navId && onSourceNavigate ? (
                      <button
                        type="button"
                        className="funding-image-space-preview__source-value"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleClose();
                          onSourceNavigate(sourceMeta);
                        }}
                      >
                        {sourceMeta.label}
                      </button>
                    ) : (
                      <span className="funding-image-space-preview__source-value">{sourceMeta.label}</span>
                    )}
                    {sourceDetail ? (
                      <span className="funding-image-space-preview__source-detail">{sourceDetail}</span>
                    ) : null}
                  </span>
                </div>
              ) : null}
              <div className="funding-image-space-preview__section-title funding-image-space-preview__section-title--spaced">
                {i18n.t('workbench:videoPreview.others')}
              </div>
              {metaRow(i18n.t('workbench:videoPreview.usageCount'), String(usage))}
              {navList.length > 1 && navIndex >= 0
                ? metaRow(
                  i18n.t('workbench:videoPreview.position'),
                  `${navIndex + 1} / ${navList.length}`,
                )
                : null}
            </aside>
          </div>

          <button
            type="button"
            className="funding-image-space-preview-nav"
            aria-label={i18n.t('workbench:imagePreview.next')}
            disabled={!canPreviewNext || loading}
            onClick={() => stepPreview(1)}
          >
            <ChevronRight size={22} strokeWidth={2.25} aria-hidden />
          </button>
        </div>

        <div className="funding-image-space-preview-footer">
          {showTrashAction ? (
            <button
              type="button"
              className="funding-image-space-preview-action funding-image-space-preview-action--trash"
              onClick={handleMoveToTrash}
            >
              {i18n.t('workbench:videoPreview.moveToTrash')}
            </button>
          ) : (
            <span className="funding-image-space-preview-footer__spacer" aria-hidden />
          )}
          <button
            type="button"
            className="funding-image-space-preview-action funding-image-space-preview-action--download"
            onClick={handleDownload}
            disabled={!previewImage}
          >
            <Download size={16} strokeWidth={2} aria-hidden />
            {i18n.t('workbench:imagePreview.download')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
